import { DatePipe } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostListener, Inject, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Canvas, FabricImage, Rect, PencilBrush, IText, Line, util } from 'fabric';

@Component({
  selector: 'app-image-edit',
  templateUrl: './image-edit.component.html',
  styleUrls: ['./image-edit.component.scss'],
  providers: [],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ImageEditComponent implements OnInit, AfterViewInit {
  datePipe = new DatePipe('en-US');
  @HostListener('window:resize', ['$event'])
  @ViewChild('canvasContainer')
  canvasContainerRef!: ElementRef;
  @ViewChild('fabricCanvas') fabricCanvasRef!: ElementRef;

  fabricCanvas!: Canvas;
  editedFile!: File;
  currentImage!: FabricImage;
  activeTool: string = '';
  selectedColor: string = '#ffff00'; // Default yellow for highlight
  selectedWidth: number = 8; // Default pencil width
  startX: number = 0;
  startY: number = 0;
  windowWidth: any = 0;
  title: any = 'Edit Image';
  base64Image: any;
  hasChanges = false;
  //#endregion
  public outIsFullScreen = true;
  constructor(
    @Inject(MAT_DIALOG_DATA) private data: any,
    private dialogRef: MatDialogRef<ImageEditComponent>
  ) {
    this.base64Image = data.data;
  }
  public onResize(event: any): void {
    this.windowWidth = event.target.innerWidth;
  }
  async ngOnInit(): Promise<void> {
    // No Fabric canvas is available yet; image loading happens after view init.
  }
  async ngAfterViewInit() {
    this.fabricCanvas = new Canvas(this.fabricCanvasRef.nativeElement, {
      selection: false,
      preserveObjectStacking: true
    });
    this.resizeCanvas();
    this.fabricCanvas.calcOffset();
    // Mobile optimization
    this.fabricCanvas.selection = false;
    this.fabricCanvas.preserveObjectStacking = true;
    await this.loadImage();
  }
  zoomIn() {
    const zoom = this.fabricCanvas.getZoom();
    this.fabricCanvas.setZoom(zoom + 0.1);
  }
  zoomOut() {
    const zoom = this.fabricCanvas.getZoom();
    this.fabricCanvas.setZoom(zoom - 0.1);
  }
  closeEditor() {
    this.activeTool = '';
    this.fabricCanvas.clear();
    this.dialogRef.close();
  }
  clearAnnotations() {
    const background = this.fabricCanvas.backgroundImage;
    this.fabricCanvas.clear();
    this.fabricCanvas.backgroundImage = background;
    this.fabricCanvas.renderAll();
  }
  undo() {
    const objects = this.fabricCanvas.getObjects();

    if (objects.length > 1) {
      this.fabricCanvas.remove(objects[objects.length - 1]);
    }
  }
  enableHighlight() {
    this.activeTool = 'highlight';
    this.disableObjectSelection();
    let rect!: Rect;
    let isDown = false;
    let startX = 0;
    let startY = 0;
    this.fabricCanvas.isDrawingMode = false;
    this.fabricCanvas.off();
    this.fabricCanvas.on('mouse:down', (event) => {
      isDown = true;
      const pointer = this.fabricCanvas.getScenePoint(event.e);
      startX = pointer.x;
      startY = pointer.y;

      rect = new Rect({
        left: startX,
        top: startY,
        width: 0,
        height: 0,
        fill: this.selectedColor + '80', // Add transparency
        stroke: this.selectedColor,
        strokeWidth: 2
      });

      this.fabricCanvas.add(rect);
    });

    this.fabricCanvas.on('mouse:move', (event) => {
      if (!isDown) return;

      const pointer = this.fabricCanvas.getScenePoint(event.e);

      const width = pointer.x - startX;

      const height = pointer.y - startY;

      rect.set({
        left: width < 0 ? pointer.x + Math.abs(width) / 2 : startX + Math.abs(width) / 2,
        top: height < 0 ? pointer.y + Math.abs(height) / 2 : startY + Math.abs(height) / 2,
        width: Math.abs(width),
        height: Math.abs(height)
      });

      this.fabricCanvas.renderAll();
    });

    this.fabricCanvas.on('mouse:up', () => {
      isDown = false;
    });
  }
  enablePen() {
    this.activeTool = 'pen';
    this.fabricCanvas.isDrawingMode = false;
    this.fabricCanvas.off('mouse:down');
    this.fabricCanvas.off('mouse:move');
    this.fabricCanvas.off('mouse:up');
    this.fabricCanvas.isDrawingMode = true;
    this.updatePenBrush();
  }
  onPenSettingChange() {
    if (this.activeTool === 'pen') {
      this.updatePenBrush();
    }
  }
  private updatePenBrush() {
    if (!this.fabricCanvas) {
      return;
    }
    const brush = new PencilBrush(this.fabricCanvas);
    brush.width = this.selectedWidth;
    brush.color = this.selectedColor;
    this.fabricCanvas.freeDrawingBrush = brush;
    this.fabricCanvas.isDrawingMode = true;
    this.fabricCanvas.calcOffset();
  }
  private async loadImage(): Promise<void> {
    const img = await FabricImage.fromURL(this.base64Image);
    const imageWidth = img.width || 1;
    const imageHeight = img.height || 1;
    const aspectRatio = imageWidth / imageHeight;
    const maxCanvasWidth = window.innerWidth * 0.9;
    const maxCanvasHeight = window.innerHeight * 0.75;
    let canvasWidth = maxCanvasWidth;
    let canvasHeight = canvasWidth / aspectRatio;

    if (canvasHeight > maxCanvasHeight) {
      canvasHeight = maxCanvasHeight;
      canvasWidth = canvasHeight * aspectRatio;
    }

    this.setCanvasDimensions(canvasWidth, canvasHeight);

    const scale = Math.min(canvasWidth / imageWidth, canvasHeight / imageHeight);
    img.scale(scale);

    img.set({
      left: canvasWidth / 2,
      top: canvasHeight / 2,
      selectable: false,
      evented: false
    });

    this.currentImage = img;
    this.fabricCanvas.clear();
    this.fabricCanvas.add(img);
    this.fabricCanvas.sendObjectToBack(img);
    this.fabricCanvas.renderAll();
  }
  disableObjectSelection() {
    this.fabricCanvas.getObjects().forEach((obj) => {
      // Keep image locked
      if (obj === this.currentImage) {
        obj.selectable = false;
        obj.evented = false;
        return;
      }
      obj.selectable = false;
      obj.evented = false;
    });

    this.fabricCanvas.discardActiveObject();
    this.fabricCanvas.renderAll();
  }
  saveEditedImage() {
    const base64 = this.fabricCanvas.toDataURL({
      format: 'png',
      multiplier: 1
    });
    const dateTime = this.datePipe.transform(new Date(), 'yyyyMMdd_HHmmss');
    this.editedFile = this.base64ToFile(base64, `captured-image_${dateTime}.png`);
    this.dialogRef.close({ files: [this.editedFile], previewImage: base64 });
  }
  resizeCanvas() {
    const width = window.innerWidth * 0.65;
    const height = window.innerHeight * 0.65;
    this.setCanvasDimensions(width, height);
  }
  private setCanvasDimensions(width: number, height: number) {
    this.fabricCanvas.setDimensions({
      width,
      height
    });
    this.fabricCanvasRef.nativeElement.style.width = `${width}px`;
    this.fabricCanvasRef.nativeElement.style.height = `${height}px`;
    this.fabricCanvas.calcOffset();
  }
  base64ToFile(dataUrl: string, filename: string): File {
    const arr = dataUrl.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';

    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  }
  closeDialog() {
    this.activeTool = '';
    this.fabricCanvas.clear();
    this.dialogRef.close();
  }
  isDialogDrag(_fullScreen: boolean) {
    this.outIsFullScreen = _fullScreen;
  }
  enableCrop() {
    this.activeTool = 'crop';
    this.disableObjectSelection();
    let cropRect: Rect | null = null;
    let isDown = false;

    this.fabricCanvas.isDrawingMode = false;
    this.fabricCanvas.off();

    this.fabricCanvas.on('mouse:down', (event) => {
      isDown = true;
      this.hasChanges = true;
      const pointer = this.fabricCanvas.getScenePoint(event.e);
      this.startX = pointer.x;
      this.startY = pointer.y;

      cropRect = new Rect({
        left: this.startX,
        top: this.startY,
        width: 0,
        height: 0,
        fill: 'rgba(0,0,0,0.3)',
        stroke: 'black',
        strokeWidth: 1,
        selectable: false,
        evented: false
      });

      this.fabricCanvas.add(cropRect);
    });

    this.fabricCanvas.on('mouse:move', (event) => {
      if (!isDown || !cropRect) return;

      const pointer = this.fabricCanvas.getScenePoint(event.e);
      const width = pointer.x - this.startX;
      const height = pointer.y - this.startY;

      cropRect.set({
        left: width < 0 ? pointer.x + Math.abs(width) / 2 : this.startX + Math.abs(width) / 2,
        top: height < 0 ? pointer.y + Math.abs(height) / 2 : this.startY + Math.abs(height) / 2,
        width: Math.abs(width),
        height: Math.abs(height)
      });

      this.fabricCanvas.renderAll();
    });

    this.fabricCanvas.on('mouse:up', () => {
      isDown = false;
    });
  }
  // applyCrop() {
  //   if (!this.fabricCanvas || !this.currentImage) return;

  //   const activeObject = this.fabricCanvas.getObjects()[this.fabricCanvas.getObjects().length - 1];
  //   if (activeObject && activeObject.type === 'rect') {
  //     const cropRect = activeObject as Rect;
  //     const { left, top, width, height } = cropRect;

  //     const croppedImage = this.currentImage.toDataURL({
  //       left: left || 0,
  //       top: top || 0,
  //       width: width || this.currentImage.width,
  //       height: height || this.currentImage.height,
  //     });

  //     FabricImage.fromURL(croppedImage, { crossOrigin: 'anonymous' }, (img: FabricImage) => {
  //       this.fabricCanvas.clear();
  //       this.fabricCanvas.add(img);
  //       this.currentImage = img;
  //       this.fabricCanvas.renderAll();
  //     });
  //   }
  // }
  async applyCrop() {
    if (!this.fabricCanvas || !this.currentImage) return;

    const objects = this.fabricCanvas.getObjects();
    const activeObject = objects[objects.length - 1];

    if (activeObject && activeObject.type === 'rect') {
      const cropRect = activeObject as Rect;
      let croppedImage = this.currentImage.toDataURL({
        format: 'png',
        left: this.startX,
        top: this.startY,
        width: cropRect.width,
        height: cropRect.height
      });
      this.base64Image = croppedImage;
      this.hasChanges = false;
      this.activeTool = '';
      this.loadImage();
    }
  }
  async ClearCrop() {
    this.activeTool = '';
    if (this.hasChanges) {
      this.undo();
    }
    this.fabricCanvas.off('mouse:down');
    this.fabricCanvas.off('mouse:move');
    this.fabricCanvas.off('mouse:up');
    this.hasChanges = false;
  }
}
